import React, { useState, useEffect } from 'react';
import './OrdersTable.css'; // Reuses general table styles
import './DevicesTable.css'; // Specific styles for devices
import DeviceDetailsPanel from './DeviceDetailsPanel';
import AddDeviceModal from './AddDeviceModal';
import Pagination from './Pagination';
import { fetchTradersData } from '../services/dataApi';
import useUrlDetails from '../hooks/useUrlDetails';

const DevicesTable = ({ data, onAddClick, onEdit, showTraderName = false, pagination, onPageChange }) => {
  const [editingDevice, setEditingDevice] = useState(null);
  const [traders, setTraders] = useState([]);
  const [tradersLoading, setTradersLoading] = useState(false);
  // Extract data - support both the new paginated format and the old format
  let devices = [];

  if (data && data.data && Array.isArray(data.data)) {
    // New paginated format
    devices = data.data;
  } else if (Array.isArray(data)) {
    // Old format - just an array
    devices = data;
  } else if (data && data.ops && data.ops.length > 0) {
    // Backward compatibility with old structure
    const firstOp = data.ops[0];
    if (firstOp.list && Array.isArray(firstOp.list)) {
      devices = firstOp.list;
    } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
      devices = firstOp.data.list;
    }
  }

  const [selectedDevice, setSelectedDevice] = useUrlDetails(devices, 'deviceId');

  // Load the trader list if the trader name needs to be shown
  useEffect(() => {
    if (showTraderName) {
      const loadTraders = async () => {
        setTradersLoading(true);
        try {
          // Load all traders (large limit to fetch all of them)
          const tradersData = await fetchTradersData(1, 1000);
          let tradersList = [];
          
          // Support the new paginated format
          if (tradersData && tradersData.data && Array.isArray(tradersData.data)) {
            tradersList = tradersData.data;
          } else if (Array.isArray(tradersData)) {
            tradersList = tradersData;
          } else if (tradersData && tradersData.ops && tradersData.ops.length > 0) {
            const firstOp = tradersData.ops[0];
            if (firstOp.list && Array.isArray(firstOp.list)) {
              tradersList = firstOp.list;
            } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
              tradersList = firstOp.data.list;
            }
          }
          setTraders(tradersList);
        } catch (err) {
          console.error('Error loading traders:', err);
          setTraders([]);
        } finally {
          setTradersLoading(false);
        }
      };
      
      loadTraders();
    }
  }, [showTraderName]);

  // Function to get the trader name by traderId
  const getTraderNameById = (traderId) => {
    if (!traderId) return '-';
    const trader = traders.find(t => {
      const id = t.traderId || t.TraderId || t.TRADER_ID || t.ref;
      return id === traderId;
    });
    if (trader) {
      return trader.name || trader.Name || trader.NAME || trader.login || trader.Login || traderId;
    }
    return traderId;
  };

  // Sort devices by create_time (newest first)
  devices.sort((a, b) => {
    const timeA = a.Created || a.created || a.create_time || 0;
    const timeB = b.Created || b.created || b.create_time || 0;
    return timeB - timeA;
  });

  if (devices.length === 0) {
    return (
      <>
        <div className="orders-table-container">
          <div className="orders-header">
            <h3>Devices List (0)</h3>
            {onAddClick && (
              <button
                className="add-button"
                onClick={onAddClick}
              >
                Add
              </button>
            )}
          </div>
          <div className="orders-empty">
            <p>No data to display</p>
          </div>
        </div>
      </>
    );
  }

  const formatCustomDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: '1rem', fontWeight: 600 }}>{day}.{month}.{year}</span>
        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{hours}:{minutes}:{seconds}</span>
      </div>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp * 1000); // If timestamp is in seconds
      if (isNaN(date.getTime())) {
        const date2 = new Date(timestamp); // Try as milliseconds
        if (isNaN(date2.getTime())) return String(timestamp);
        return formatCustomDate(date2);
      }
      return formatCustomDate(date);
    } catch {
      return String(timestamp);
    }
  };

  // Define columns for display
  const displayColumns = [
    {
      key: 'Created', label: 'Created', getValue: (device) => {
        const created = device.Created || device.created || device.create_time || null;
        return formatDate(created);
      }
    },
    ...(showTraderName ? [{
      key: 'TraderName',
      label: 'Trader Name',
      getValue: (device) => {
        const traderId = device.traderId || device.TraderId || device.TRADER_ID;
        return tradersLoading ? 'Loading...' : getTraderNameById(traderId);
      }
    }] : []),
    {
      key: 'DeviceName', label: 'Device Name', getValue: (device) => {
        return device.userDeviceName || device.UserDeviceName || device.user_device_name || '-';
      }
    },
    {
      key: 'DeviceModel', label: 'Device Model', getValue: (device) => {
        return device.device_model || device.deviceModel || device.DeviceModel || '-';
      }
    },
    {
      key: 'Status', label: 'Status', getValue: (device) => {
        return device.status || device.Status || device.STATUS || '-';
      }
    }
  ];

  const totalItems = pagination?.total || devices.length;

  return (
    <>
      <div className="orders-table-container">
        <div className="orders-header">
          <h3>Devices List ({totalItems})</h3>
          {onAddClick && (
            <button
              className="add-button"
              onClick={onAddClick}
            >
              Add
            </button>
          )}
        </div>
        <div className="table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                {displayColumns.map((column) => (
                  <th key={column.key} className={`header-${column.key.toLowerCase()}`}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {devices.map((device, index) => {
                const status = (device.status || device.Status || device.STATUS || '').toLowerCase();
                // Normalize status to 'active' or 'inactive'
                const normalizedStatus = status === 'active' ? 'active' : (status === 'inactive' ? 'inactive' : '');
                return (
                  <tr
                    key={device.id || device.ref || index}
                    className="order-row"
                    onClick={() => setSelectedDevice(device)}
                  >
                    {displayColumns.map((column) => {
                      const isStatusColumn = column.key === 'Status';
                      const statusClass = isStatusColumn && normalizedStatus ? `status-${normalizedStatus}` : '';
                      return (
                        <td
                          key={column.key}
                          className={`column-${column.key.toLowerCase()} ${statusClass}`}
                          data-label={column.label}
                        >
                          {column.getValue(device)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pagination && onPageChange && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            itemsPerPage={pagination.limit}
            totalItems={pagination.total}
          />
        )}
      </div>
      {selectedDevice && (
        <DeviceDetailsPanel
          device={selectedDevice}
          onClose={() => setSelectedDevice(null)}
          onEdit={onEdit ? (device) => {
            setEditingDevice(device);
            setSelectedDevice(null); // Close details panel
          } : undefined}
        />
      )}
      {onEdit && <AddDeviceModal
        isOpen={!!editingDevice}
        onClose={() => setEditingDevice(null)}
        onSubmit={onEdit}
        initialData={editingDevice}
        isEdit={true}
      />}
    </>
  );
};

export default DevicesTable;
